import { Reducer } from 'redux'
import { Layout } from 'common/types/reactGridLayout'

import { InterfaceActionTypes, InterfaceState } from './types'
import {
  GenericComponent,
  IRenderComponent,
  IMinBaseComponent,
  SelectorFormats,
  ComponentTypes,
  SelectorResult,
  IndexesAndOptions,
  FilterComponent
} from 'pages/interfaces/components/types'
import {
  FilteredComponentValue,
  GridLayout,
  InterfaceEvent,
  InterfaceEventType
} from 'pages/interfaces/containers/types'
import { randomId } from 'common/helpers/strings'
import { defaultLayout } from 'pages/interfaces/helpers'
import { TabItem } from 'common/types/model'
import { ActiveTab } from 'common/types/interface'
import { evictCachedInterfaces, omitInterfaces } from './cache'

// Formats that render all options inline and therefore cache them in selectorOptionsMax
const CACHED_OPTION_FORMATS = [
  SelectorFormats.SelectableChips,
  SelectorFormats.Checkbox,
  SelectorFormats.Slider
]

/**
 * Everything cached per interface: the widget results plus the bookkeeping that
 * hangs off them. Returns only the slices that actually changed, so it can be
 * spread into a reducer's next state.
 */
const dropCachedInterfaces = (
  state: InterfaceState,
  interfaceIds: string[]
): Partial<InterfaceState> => {
  if (interfaceIds.length === 0) return {}
  const applicationComponents = omitInterfaces(state.applicationComponents, interfaceIds)
  const componentSizes = omitInterfaces(state.componentSizes, interfaceIds)
  const interfaceViewStates = omitInterfaces(state.interfaceViewStates, interfaceIds)
  const dropped: Partial<InterfaceState> = {}
  if (applicationComponents !== state.applicationComponents) {
    dropped.applicationComponents = applicationComponents
  }
  if (componentSizes !== state.componentSizes) {
    dropped.componentSizes = componentSizes
  }
  if (interfaceViewStates !== state.interfaceViewStates) {
    dropped.interfaceViewStates = interfaceViewStates
  }
  return dropped
}

const initialState: InterfaceState = {
  selectedComponent: undefined,
  componentsLoading: undefined,
  tabs: [],
  activeTabs: [],
  list: [],
  editMode: false,
  tabPanelValue: 0,
  refreshCurrentInterface: false,
  refreshCurrentInterfaceLayout: false,
  scenariosSelectionNonce: 0,
  reloadLayout: 0,
  filterComponentState: undefined
}

const reducer: Reducer<InterfaceState> = (
  state: InterfaceState = initialState,
  action: { type: InterfaceActionTypes; payload?: any }
) => {
  switch (action.type) {
    case InterfaceActionTypes.SELECT_COMPONENT: {
      return {
        ...state,
        ...{ selectedComponent: action.payload }
      }
    }
    case InterfaceActionTypes.SET_INDEX_FILTER_DATA: {
      return {
        ...state,
        indexFilter: action.payload
      }
    }
    case InterfaceActionTypes.SET_DRILLDOWN_OPTIONS: {
      return {
        ...state,
        drilldownOptions: action.payload
      }
    }
    case InterfaceActionTypes.UPDATE_LAYOUT: {
      const interfaceId: string = action.payload.interfaceId
      const newLayout: GridLayout = action.payload.newLayout
      const triggerData: InterfaceEvent = action.payload.triggerData
      const triggerEvent: (triggerData: InterfaceEvent) => Promise<void> =
        action.payload.triggerEvent
      const eventType = triggerData.event_type
      const activeLayout = state.activeTabs?.find(
        (tab) => tab.activeTabId === interfaceId && tab.currentTab
      ) || {
        activeTabId: interfaceId,
        currentTab: false,
        components: defaultLayout.components,
        properties: defaultLayout.properties,
        // Without it this stand-in would look like the least recently used tab
        // and be the first one the cache drops.
        lastAccessedAt: Date.now()
      }
      // HTML Interfaces don't have drag&drop components — the iframe's
      // content is in `properties.html` and a fresh `session_token` arrives
      // on every layout fetch. Skip the component-diff logic and just
      // replace properties; the renderer keys the iframe on session_token
      // so a fresh layout forces a re-mount automatically.
      if (newLayout?.properties?.interface_type === 'html') {
        activeLayout.properties = newLayout.properties
        activeLayout.components = []
        const activeTabs = state.activeTabs || []
        const index = activeTabs.findIndex((item) => item.activeTabId === interfaceId)
        if (index !== -1) {
          activeTabs[index] = activeLayout
        } else {
          activeTabs.push(activeLayout)
        }
        return { ...state, activeTabs: [...activeTabs] }
      }
      let selectedComponent = state.selectedComponent
      const componentesToAdd: IMinBaseComponent[] = []
      // Remove
      activeLayout.components = activeLayout.components.filter((layoutItem) =>
        newLayout.components.some(
          (newLayoutItem) => newLayoutItem.component_id === layoutItem.component_id
        )
      )
      // Remove changed component
      if (triggerData.event_type === InterfaceEventType.ChangeComponent) {
        activeLayout.components = activeLayout.components.filter(
          (layoutItem) => layoutItem.component_id !== triggerData.params.component_id
        )
      }
      // Update existing components with new properties (e.g. after an interface restore)
      if (eventType === InterfaceEventType.RefreshEvent) {
        activeLayout.components = activeLayout.components.map((layoutItem) => {
          const updated = newLayout.components.find(
            (newItem) => newItem.component_id === layoutItem.component_id
          )
          return updated ? { ...updated, renderKey: randomId() } : layoutItem
        })
      }
      // Add
      const newItems = newLayout.components.filter(
        (newLayoutItem) =>
          !activeLayout.components.some(
            (layoutItem) => layoutItem.component_id === newLayoutItem.component_id
          )
      )
      if (newItems?.length > 0) {
        newItems.forEach((item) => {
          item.renderKey = randomId()
        })
        componentesToAdd.push(...newItems)
        activeLayout.components = activeLayout.components.concat(componentesToAdd)
      }
      if (eventType === InterfaceEventType.ChangeComponent) {
        // Change selected component
        const componentTab = newLayout.components.find(
          (item) => item.component_id === triggerData.params.component_id
        )
        if (componentTab) {
          selectedComponent =
            state.applicationComponents?.[interfaceId]?.[componentTab.component_id]
        }
      } else if (
        eventType === InterfaceEventType.CreateComponent &&
        newLayout.components.length > 0
      ) {
        // Select last component
        const componentTab = newLayout.components[newLayout.components.length - 1]
        if (componentTab) {
          selectedComponent =
            state.applicationComponents?.[interfaceId]?.[componentTab.component_id]
        }
      }
      componentesToAdd.forEach((component) => {
        const initialUpdate: InterfaceEvent = {
          event_type: InterfaceEventType.InitialUpdate,
          params: {
            component_id: component.component_id
          }
        }
        triggerEvent(initialUpdate)
      })
      const activeTabs = state.activeTabs || []
      const index = activeTabs.findIndex((item) => item.activeTabId === interfaceId)
      if (index !== -1) {
        activeTabs[index] = activeLayout
      } else {
        activeTabs.push(activeLayout)
      }

      return {
        ...state,
        selectedComponent,
        activeTabs: [...activeTabs]
      }
    }
    case InterfaceActionTypes.UPDATE_LAYOUT_POSITIONS: {
      const interfaceId: string = action.payload.interfaceId
      const newLayout: Layout[] = action.payload.newLayout
      const activeLayout = state.activeTabs?.find(
        (tab) => tab.activeTabId === interfaceId && tab.currentTab
      ) || {
        activeTabId: interfaceId,
        currentTab: false,
        components: defaultLayout.components,
        properties: defaultLayout.properties,
        // Without it this stand-in would look like the least recently used tab
        // and be the first one the cache drops.
        lastAccessedAt: Date.now()
      }
      const nextComponents = activeLayout.components.map((component) => {
        const itemLayout = newLayout.find(
          (layoutItem) => layoutItem.i.split('.')[0] === component.component_id
        )
        if (!itemLayout) {
          return component
        }

        return {
          ...component,
          x: itemLayout.x,
          y: itemLayout.y,
          h: itemLayout.h,
          w: itemLayout.w
        }
      })
      const nextActiveLayout = {
        ...activeLayout,
        components: nextComponents
      }
      const currentApplicationComponents = state.applicationComponents?.[interfaceId] || {}
      const nextApplicationComponents = Object.fromEntries(
        Object.entries(currentApplicationComponents).map(([componentId, appComponent]) => {
          const itemLayout = newLayout.find(
            (layoutItem) => layoutItem.i.split('.')[0] === componentId
          )
          if (!itemLayout) {
            return [componentId, appComponent]
          }

          return [
            componentId,
            {
              ...appComponent,
              x: itemLayout.x,
              y: itemLayout.y,
              h: itemLayout.h,
              w: itemLayout.w
            }
          ]
        })
      )
      const activeTabs = state.activeTabs || []
      const index = activeTabs.findIndex((item) => item.activeTabId === interfaceId)
      if (index !== -1) {
        activeTabs[index] = nextActiveLayout
      } else {
        activeTabs.push(nextActiveLayout)
      }

      return {
        ...state,
        activeTabs: [...activeTabs],
        applicationComponents: {
          ...state.applicationComponents,
          [interfaceId]: nextApplicationComponents
        }
      }
    }
    case InterfaceActionTypes.UPDATE_DEFAULT_INTERFACE_LAYOUT: {
      const newComponentResult: IRenderComponent = action.payload.result
      const defaultInterfaceLayout = state.defaultInterfaceLayout
      let selectedComponent = state.selectedComponent
      if (defaultInterfaceLayout) {
        const component = defaultInterfaceLayout.components.find(
          (layoutItemComponent) =>
            layoutItemComponent.component_id === newComponentResult.component_id
        )
        if (component) {
          const position = defaultInterfaceLayout.components.indexOf(component)
          const appComponent =
            state.applicationComponents?.[action.payload.interfaceId]?.[component.component_id]
          if (appComponent) {
            const newComponent = Object.assign({}, appComponent, newComponentResult)
            newComponent.renderKey = randomId()
            if (
              appComponent?.result &&
              newComponentResult.error_code &&
              newComponentResult.error_detail
            ) {
              newComponent.error_code = newComponentResult.error_code
              newComponent.error_detail = newComponentResult.error_detail
            }
            newComponent.isLoading = false
            defaultInterfaceLayout.components[position] = newComponent
            if (selectedComponent?.component_id === newComponent.component_id) {
              selectedComponent = newComponent
            }
          }
        }
      }
      return {
        ...state,
        defaultInterfaceLayout: { ...defaultInterfaceLayout }
      }
    }
    case InterfaceActionTypes.SET_COMPONENT_IS_LOADING: {
      return {
        ...state,
        componentsLoading: action.payload.componentsList
      }
    }
    case InterfaceActionTypes.SET_FILTER_COMPONENT_IS_LOADING: {
      return {
        ...state,
        filterComponentsLoading: action.payload
      }
    }
    case InterfaceActionTypes.SET_DEFAULT_INTERFACE_LAYOUT: {
      return {
        ...state,
        defaultInterfaceLayout: action.payload
      }
    }
    case InterfaceActionTypes.SAVE_LAYOUT: {
      const interfaceId: string = action.payload.interfaceId
      const newLayoutToSave: GridLayout = action.payload.newLayout
      const activeLayout = state.activeTabs?.find(
        (tab) => tab.activeTabId === interfaceId && tab.currentTab
      ) || {
        activeTabId: interfaceId,
        components: defaultLayout.components,
        properties: defaultLayout.properties
      }
      const currentComponentsById = new Map(
        activeLayout.components.map((component) => [component.component_id, component])
      )
      const savedComponents = newLayoutToSave.components.map((component) => {
        const currentComponent = currentComponentsById.get(component.component_id)

        return {
          ...component,
          x: currentComponent?.x ?? component.x,
          y: currentComponent?.y ?? component.y,
          w: currentComponent?.w ?? component.w,
          h: currentComponent?.h ?? component.h,
          properties: {
            ...component.properties
          }
        }
      })
      const nextActiveLayout = {
        ...activeLayout,
        properties: {
          ...newLayoutToSave.properties
        },
        components: savedComponents
      }
      const currentApplicationComponents = state.applicationComponents?.[interfaceId] || {}
      const nextApplicationComponents = Object.fromEntries(
        Object.entries(currentApplicationComponents).map(([componentId, appComponent]) => {
          const savedComponent = savedComponents.find(
            (componentToSave) => componentToSave.component_id === componentId
          )
          if (!savedComponent) {
            return [componentId, appComponent]
          }

          return [
            componentId,
            {
              ...appComponent,
              x: savedComponent.x,
              y: savedComponent.y,
              w: savedComponent.w,
              h: savedComponent.h,
              properties: {
                ...appComponent.properties,
                ...savedComponent.properties
              }
            }
          ]
        })
      )
      const activeTabs = state.activeTabs || []
      const index = activeTabs.findIndex((item) => item.activeTabId === interfaceId)
      if (index !== -1) {
        activeTabs[index] = nextActiveLayout
      } else {
        activeTabs.push(nextActiveLayout)
      }

      return {
        ...state,
        activeTabs: [...activeTabs],
        applicationComponents: {
          ...state.applicationComponents,
          [interfaceId]: nextApplicationComponents
        }
      }
    }
    case InterfaceActionTypes.SET_INTERFACE_WIDGET_EXPANDED: {
      return {
        ...state,
        interfaceWidgetExpanded: { componentId: action.payload }
      }
    }
    case InterfaceActionTypes.SET_COMPONENT_TO_EDIT: {
      return {
        ...state,
        componentToEdit: { ...action.payload }
      }
    }
    case InterfaceActionTypes.COPY_STYLES: {
      return {
        ...state,
        stylesToCopy: { ...action.payload }
      }
    }
    case InterfaceActionTypes.COPY_GENERAL_PROPERTIES: {
      return {
        ...state,
        generalPropsToCopy: { ...action.payload }
      }
    }
    case InterfaceActionTypes.SET_RECENTLY_USED_COLORS: {
      const newColors = [...(state.recentlyUsedColors || [])]
      newColors.push(action.payload)
      return {
        ...state,
        recentlyUsedColors: newColors
      }
    }
    case InterfaceActionTypes.SET_INTERFACE_TABS: {
      const activeTabs = state.activeTabs || []
      // remove active tab if it is not in the new tabs
      const newTabsIds = new Set(action.payload.map((tab: TabItem) => tab.id))
      const newActiveTabs = activeTabs.filter((tab) => newTabsIds.has(tab.activeTabId))
      // An interface that is no longer among the open tabs is closed, not just
      // uncached: nothing can reach its results again, so they must go with it.
      const closedIds = activeTabs
        .filter((tab) => !newTabsIds.has(tab.activeTabId))
        .map((tab) => tab.activeTabId)
      return {
        ...state,
        tabs: action.payload,
        activeTabs: newActiveTabs,
        ...dropCachedInterfaces(state, closedIds)
      }
    }
    case InterfaceActionTypes.SET_OPEN_INTERFACE_CURRENT_TAB: {
      return {
        ...state,
        openInterfaceCurrentTab: action.payload
      }
    }
    case InterfaceActionTypes.SET_INTERFACE_OPENED_FROM_MANAGER: {
      return {
        ...state,
        interfaceOpenedFromManager: action.payload
      }
    }
    case InterfaceActionTypes.SET_INTERFACE_OPENED_FROM_CREATION: {
      return {
        ...state,
        interfaceOpenedFromCreation: action.payload
      }
    }
    case InterfaceActionTypes.SET_ALL_INTERFACES: {
      return {
        ...state,
        list: action.payload
      }
    }
    case InterfaceActionTypes.SET_INTERFACE_WITH_CHANGES: {
      return {
        ...state,
        interfaceWithChanges: action.payload
      }
    }
    case InterfaceActionTypes.SET_CLICKED_FROM_TOPBAR: {
      return {
        ...state,
        clickedFromTopbar: action.payload
      }
    }
    case InterfaceActionTypes.CHANGE_DEFAULT_INTERFACE: {
      return {
        ...state,
        changeDefaultInterface: action.payload
      }
    }
    case InterfaceActionTypes.SET_ACCORDION_MENU_ITEMS: {
      const accordionMenu = state.accordionMenu || []
      const currentItemIndex = accordionMenu.findIndex((item) => item.id === action.payload.id)
      const newAccordionMenu =
        currentItemIndex !== -1
          ? accordionMenu.map((item, index) => (index === currentItemIndex ? action.payload : item))
          : [...accordionMenu, action.payload]
      return {
        ...state,
        accordionMenu: newAccordionMenu
      }
    }
    case InterfaceActionTypes.SET_MENU_FOLDER_HISTORY: {
      const componentId = action.payload.componentId
      const interfaceId = action.payload.interfaceId
      const folders = action.payload.folders
      const currentFolder = action.payload.currentFolder
      const menuFolderHistory = state.menuFolderHistory || {}
      menuFolderHistory[`${interfaceId}-${componentId}`] = { folders, currentFolder }
      return {
        ...state,
        menuFolderHistory: { ...menuFolderHistory }
      }
    }
    case InterfaceActionTypes.SET_REFRESH_CURRENT_INTERFACE: {
      return {
        ...state,
        refreshCurrentInterface: action.payload
      }
    }
    case InterfaceActionTypes.SET_REFRESH_CURRENT_INTERFACE_LAYOUT: {
      return {
        ...state,
        refreshCurrentInterfaceLayout: action.payload
      }
    }
    case InterfaceActionTypes.RECEIVE_HTML_INTERFACE_BROADCAST: {
      const seq = (state.htmlInterfaceBroadcast?.seq ?? 0) + 1
      return { ...state, htmlInterfaceBroadcast: { ...action.payload, seq } }
    }
    case InterfaceActionTypes.NOTIFY_SCENARIOS_CHANGED: {
      return {
        ...state,
        scenariosSelectionNonce: (state.scenariosSelectionNonce || 0) + 1
      }
    }
    case InterfaceActionTypes.NOTIFY_INTERFACES_LIST_CHANGED: {
      return { ...state, interfacesListNonce: (state.interfacesListNonce ?? 0) + 1 }
    }
    case InterfaceActionTypes.MARK_HTML_INTERFACE_PROMPTED: {
      const id: string = action.payload
      const prev = state.promptedHtmlInterfaces || []
      if (prev.indexOf(id) !== -1) return state
      return {
        ...state,
        promptedHtmlInterfaces: [...prev, id]
      }
    }
    case InterfaceActionTypes.SET_HTML_INTERFACE_SNAPSHOT: {
      const { interfaceId, python_code, html } = action.payload
      return {
        ...state,
        htmlInterfaceSnapshots: {
          ...(state.htmlInterfaceSnapshots || {}),
          [interfaceId]: { python_code, html }
        }
      }
    }
    case InterfaceActionTypes.SAVE_INTERFACE_SCROLL: {
      const { interfaceId, scrollTop, scrollLeft } = action.payload
      return {
        ...state,
        interfaceViewStates: {
          ...state.interfaceViewStates,
          [interfaceId]: {
            ...state.interfaceViewStates?.[interfaceId],
            scrollTop,
            scrollLeft
          }
        }
      }
    }
    case InterfaceActionTypes.SAVE_COMPONENT_VIEW_STATE: {
      const { interfaceId, componentId, viewState } = action.payload
      const interfaceViewState = state.interfaceViewStates?.[interfaceId]
      return {
        ...state,
        interfaceViewStates: {
          ...state.interfaceViewStates,
          [interfaceId]: {
            ...interfaceViewState,
            components: {
              ...interfaceViewState?.components,
              [componentId]: {
                ...interfaceViewState?.components?.[componentId],
                ...viewState
              }
            }
          }
        }
      }
    }
    case InterfaceActionTypes.CLEAR_HTML_INTERFACE_SNAPSHOT: {
      const id: string = action.payload
      if (!state.htmlInterfaceSnapshots || !(id in state.htmlInterfaceSnapshots)) {
        return state
      }
      const next = { ...state.htmlInterfaceSnapshots }
      delete next[id]
      return { ...state, htmlInterfaceSnapshots: next }
    }
    case InterfaceActionTypes.SET_EXTERNAL_LAYOUT: {
      return {
        ...state,
        externalLayout: action.payload
      }
    }
    case InterfaceActionTypes.SET_INTERFACE_FROM_LINK: {
      return {
        ...state,
        interfaceFromLink: action.payload
      }
    }
    case InterfaceActionTypes.SET_EXTERNAL_INTERFACE_ID: {
      return {
        ...state,
        externalInterfaceId: action.payload
      }
    }
    case InterfaceActionTypes.SET_LAST_ACCESSED_PATH: {
      return {
        ...state,
        lastAccessedPath: action.payload
      }
    }
    case InterfaceActionTypes.SET_EDIT_MODE: {
      return {
        ...state,
        editMode: action.payload
      }
    }
    case InterfaceActionTypes.SET_TAB_PANEL_VALUE: {
      return {
        ...state,
        tabPanelValue: action.payload
      }
    }
    case InterfaceActionTypes.SET_CURRENT_NAVIGATION_HISTORY: {
      return {
        ...state,
        currentNavigationHistory: action.payload
      }
    }
    case InterfaceActionTypes.SET_EDITOR_DYNAMIC_HTML: {
      return {
        ...state,
        componentDynamicHtml: action.payload
      }
    }
    case InterfaceActionTypes.SET_VERTICAL_DYNAMIC_HTML_SIZE: {
      return {
        ...state,
        verticalDynamicHtmlSize: action.payload
      }
    }
    case InterfaceActionTypes.SET_HORIZONTAL_DYNAMIC_HTML_SIZE: {
      return {
        ...state,
        horizontalDynamicHtmlSize: action.payload
      }
    }
    case InterfaceActionTypes.SET_OPEN_DEFAULT_INTERFACE_FROM_TOPBAR: {
      return {
        ...state,
        openDefaultInterfaceFromTopbar: action.payload
      }
    }
    case InterfaceActionTypes.SET_VERTICAL_ACTIONS_DYNAMIC_HTML_SIZE: {
      return {
        ...state,
        verticalActionsDynamicHtmlSize: action.payload
      }
    }
    case InterfaceActionTypes.SET_HORIZONTAL_ACTIONS_DYNAMIC_HTML_SIZE: {
      return {
        ...state,
        horizontalActionsDynamicHtmlSize: action.payload
      }
    }
    case InterfaceActionTypes.SET_EDITING_INTERFACE: {
      return {
        ...state,
        editingInterface: action.payload
      }
    }
    case InterfaceActionTypes.SET_SCROLL_ROW_TABLE: {
      return {
        ...state,
        scrollRowTable: action.payload
      }
    }
    case InterfaceActionTypes.SET_SCROLL_COLUMN_TABLE: {
      return {
        ...state,
        scrollColumnTable: action.payload
      }
    }
    case InterfaceActionTypes.LAST_COMPONENT_DRILLDOWNED: {
      return {
        ...state,
        lastComponentDrilldowned: action.payload
      }
    }
    case InterfaceActionTypes.SET_ACTIVE_TAB: {
      const incomingTab: ActiveTab = action.payload
      const activeTabs = state.activeTabs || []
      const openedAt = Date.now()
      const markCurrentTab = (tabs: ActiveTab[]) =>
        tabs.map((tab) => {
          const isCurrent = tab.activeTabId === incomingTab.activeTabId
          if (isCurrent) {
            return { ...tab, currentTab: true, lastAccessedAt: openedAt }
          }
          return tab.currentTab ? { ...tab, currentTab: false } : tab
        })

      if (activeTabs.some((item) => item.activeTabId === incomingTab.activeTabId)) {
        return {
          ...state,
          activeTabs: markCurrentTab(activeTabs)
        }
      }

      incomingTab.components.forEach((item) => {
        item.renderKey = randomId()
      })
      const { activeTabs: cachedTabs, evictedIds } = evictCachedInterfaces(
        [
          ...markCurrentTab(activeTabs),
          { ...incomingTab, currentTab: true, lastAccessedAt: openedAt }
        ],
        state.componentSizes,
        [incomingTab.activeTabId, state.defaultInterfaceLayout?.properties?.id]
      )
      return {
        ...state,
        activeTabs: cachedTabs,
        ...dropCachedInterfaces(state, evictedIds)
      }
    }
    case InterfaceActionTypes.SELECTOR_OPTIONS_MAX: {
      const prevState = state.selectorOptionsMax || []
      if (!action.payload) {
        return {
          ...state,
          selectorOptionsMax: undefined
        }
      } else {
        const existingIndex = prevState.findIndex(
          (item) => item.componentId === action.payload.componentId
        )
        const nextState =
          existingIndex !== -1
            ? [
                ...prevState.slice(0, existingIndex),
                action.payload,
                ...prevState.slice(existingIndex + 1)
              ]
            : [...prevState, action.payload]
        return {
          ...state,
          selectorOptionsMax: nextState
        }
      }
    }
    case InterfaceActionTypes.UPDATE_ACTIVE_TAB: {
      const activeTabs = state.activeTabs || []
      const index = activeTabs.findIndex((item) => item.activeTabId === action.payload.activeTabId)
      if (index !== -1) {
        return {
          ...state,
          activeTabs: [
            ...activeTabs.slice(0, index),
            // lastAccessedAt is not part of the payload and must survive, or the
            // tab would look like the least recently used one to the cache.
            { ...action.payload, lastAccessedAt: activeTabs[index].lastAccessedAt },
            ...activeTabs.slice(index + 1)
          ]
        }
      }
      return {
        ...state,
        activeTabs
      }
    }
    case InterfaceActionTypes.UPDATE_ACTIVE_TAB_PROPERTIES: {
      const { activeTabId, properties } = action.payload
      if (!state.activeTabs?.some((item) => item.activeTabId === activeTabId)) {
        return state
      }
      return {
        ...state,
        activeTabs: state.activeTabs.map((item) =>
          item.activeTabId === activeTabId
            ? { ...item, properties: { ...item.properties, ...properties } }
            : item
        )
      }
    }
    case InterfaceActionTypes.REMOVE_ACTIVE_TAB: {
      const activeTabs = state.activeTabs || []
      if (!activeTabs.some((item) => item.activeTabId === action.payload)) {
        return state
      }
      // Only the cached layout goes. Flows that drop the tab to force a reload
      // (cancelling interface edits, changing the custom view) re-add it once the
      // layout request returns, and keeping the results is what lets the widgets
      // show data instead of staying empty while the fresh ones arrive.
      //
      // The invariant this leaves to the callers: while an interface is out of
      // `activeTabs` its results are invisible to the cache budget, which only
      // walks the tabs. Every caller that drops a tab without reloading it must
      // dispatch REMOVE_APPLICATION_COMPONENTS alongside - the close flows and the
      // discard-then-switch path do. Widening the eviction to reclaim them here is
      // not an option: the reload flows leave the interface orphaned across the
      // layout request, and a result arriving in that window would drop exactly
      // the results the reload is about to show.
      return {
        ...state,
        activeTabs: activeTabs.filter((item) => item.activeTabId !== action.payload)
      }
    }
    case InterfaceActionTypes.REMOVE_ACTIVES_TABS: {
      const defaultInterfaceLayout = state.defaultInterfaceLayout || defaultLayout
      if (defaultInterfaceLayout && defaultInterfaceLayout.properties.id) {
        const activeDefaultTab = {
          activeTabId: defaultInterfaceLayout.properties.id,
          currentTab: true,
          components: defaultInterfaceLayout.components,
          properties: defaultInterfaceLayout.properties,
          lastAccessedAt: Date.now()
        }
        return {
          ...state,
          activeTabs: [activeDefaultTab],
          ...dropCachedInterfaces(
            state,
            (state.activeTabs ?? [])
              .map((tab) => tab.activeTabId)
              .filter((id) => id !== activeDefaultTab.activeTabId)
          )
        }
      }
      return state
    }
    case InterfaceActionTypes.SET_FILTERED_COMPONENT_VALUE: {
      return {
        ...state,
        filteredComponentValue: action.payload
      }
    }
    case InterfaceActionTypes.SET_SELECTED_OPTION_STATE: {
      return {
        ...state,
        selectedOptionState: action.payload
      }
    }
    case InterfaceActionTypes.LOADING_TAB_MENU: {
      return {
        ...state,
        loadingTabMenu: action.payload
      }
    }
    case InterfaceActionTypes.SET_INTERFACE_DETACHED_SIDEBAR: {
      return {
        ...state,
        interfaceDetachedSidebar: action.payload
      }
    }
    case InterfaceActionTypes.SET_FILTERS_APPLIED: {
      return {
        ...state,
        filtersApplied: action.payload
      }
    }
    case InterfaceActionTypes.SET_APPLICATION_COMPONENT: {
      const interfaceId = action.payload.interfaceId
      const componentId = action.payload.appComponent.component_id
      const previousComponent = state.applicationComponents?.[interfaceId]?.[componentId] as
        | IRenderComponent
        | undefined
      // Keep the existing renderKey across updates (any component type) to avoid
      // unmounting/remounting the widget - and losing its internal state (scroll,
      // open menus, form data, etc.) - on every routine result update. Only
      // components seen for the first time get a freshly generated one.
      action.payload.appComponent.renderKey = previousComponent?.renderKey ?? randomId()
      action.payload.appComponent.isLoading = false

      let selectedComponent = state.selectedComponent
      if (
        !state.filtersApplied ||
        state.selectedComponent?.component_id === action.payload.appComponent.component_id
      ) {
        selectedComponent = action.payload.appComponent
      }

      let componentsLoading = state.componentsLoading
      let selectorOptionsMax = state.selectorOptionsMax
      if (componentsLoading && componentsLoading.includes(componentId)) {
        componentsLoading = componentsLoading.filter((item) => item !== componentId)
      }
      // These formats render every option inline, so they cache them in selectorOptionsMax
      // instead of refetching on open. When a dependent selector recalculates (its parent
      // changed), the cache must be invalidated or it keeps showing the previous options.
      const appComponent = action.payload.appComponent
      const selectorFormat = ((appComponent?.result as unknown) as SelectorResult)?.format
      if (
        appComponent.component_type === ComponentTypes.Core_Selector &&
        CACHED_OPTION_FORMATS.includes(selectorFormat as SelectorFormats)
      ) {
        const selectedOptionMax = state.selectorOptionsMax?.find(
          (item) => item.componentId === appComponent.component_id
        )
        if (selectedOptionMax) {
          const optionsAndIndexes = ((appComponent?.result as unknown) as SelectorResult)
            ?.options_and_indexes
          const convertedObject =
            optionsAndIndexes?.reduce((acc: { [key: number]: string }, option) => {
              acc[option.index] = option.value
              return acc
            }, {} as IndexesAndOptions) || {}
          const areEqual =
            JSON.stringify(selectedOptionMax.results) === JSON.stringify(convertedObject)
          // Replace with a new object: mutating the cached one keeps useSelector's reference
          // equality intact and the subscribed selector never re-renders.
          if (!areEqual && Object.keys(convertedObject).length > 0) {
            selectorOptionsMax = [
              ...(state.selectorOptionsMax ?? []).filter(
                (item) => item.componentId !== appComponent.component_id
              ),
              { componentId: appComponent.component_id, results: convertedObject }
            ]
          }
        }
      }

      if (
        state?.filteredComponentValue &&
        state.filteredComponentValue.componentId === componentId
      ) {
        // This if statement was added to fix a visual bug when no options are selected from the transfer list.
        if (
          ![ComponentTypes.Core_Selector, ComponentTypes.Core_Index].includes(
            appComponent.component_type
          )
        ) {
          action.payload.appComponent.result.selected_values = state.filteredComponentValue?.values
          if (state.filteredComponentValue?.indexes) {
            action.payload.appComponent.result.selected_indexes =
              state.filteredComponentValue.indexes
          }
        }
        delete state.filteredComponentValue
      }

      let filterComponentState = state.filterComponentState
      let filterComponentIds = state.filterComponentIds
      let positionFilterSticky = state.positionFilterSticky
      if (
        appComponent.component_type === ComponentTypes.Core_Filter &&
        appComponent?.result?.filter_components
      ) {
        positionFilterSticky = appComponent.properties.position_sticky
        filterComponentState = appComponent.result.filter_components as FilterComponent[]
        filterComponentIds = filterComponentState.map((item) => item.component_id)
      }
      const payloadChars: number | undefined = action.payload.payloadChars
      const componentSizes =
        payloadChars === undefined
          ? state.componentSizes
          : {
              ...state.componentSizes,
              [interfaceId]: {
                ...state.componentSizes?.[interfaceId],
                [componentId]: payloadChars
              }
            }
      const nextState: InterfaceState = {
        ...state,
        // Kept as a spread (an empty object when there is no selection) because
        // downstream consumers rely on it being truthy.
        selectedComponent: { ...selectedComponent } as GenericComponent,
        componentsLoading: componentsLoading ? [...componentsLoading] : undefined,
        selectorOptionsMax: selectorOptionsMax ? [...selectorOptionsMax] : undefined,
        filterComponentState: filterComponentState,
        filterComponentIds: filterComponentIds,
        positionFilterSticky: positionFilterSticky,
        componentSizes,
        applicationComponents: {
          ...state.applicationComponents,
          [interfaceId]: {
            ...state.applicationComponents?.[interfaceId],
            [componentId]: action.payload.appComponent
          }
        }
      }
      // Results keep arriving while the user stays on one interface, so the memory
      // budget has to be enforced here too and not only when a tab is opened.
      const {
        activeTabs: tabsWithinBudget,
        evictedIds
      } = evictCachedInterfaces(nextState.activeTabs ?? [], nextState.componentSizes, [
        interfaceId,
        nextState.activeTabs?.find((tab) => tab.currentTab)?.activeTabId,
        state.defaultInterfaceLayout?.properties?.id
      ])
      if (evictedIds.length === 0) return nextState
      return {
        ...nextState,
        activeTabs: tabsWithinBudget,
        ...dropCachedInterfaces(nextState, evictedIds)
      }
    }
    case InterfaceActionTypes.UPDATE_APPLICATION_COMPONENT: {
      const appComponents = action.payload.appComponent
        ? {
            [action.payload.appComponent.component_id]: {
              ...state.applicationComponents?.[action.payload.interfaceId]?.[
                action.payload.appComponent?.component_id
              ],
              ...action.payload.appComponent
            }
          }
        : {}

      return {
        ...state,
        applicationComponents: {
          ...state.applicationComponents,
          [action.payload.interfaceId]: {
            ...state.applicationComponents?.[action.payload.interfaceId],
            ...appComponents
          }
        }
      }
    }
    case InterfaceActionTypes.REMOVE_APPLICATION_COMPONENTS: {
      const interfaceId = action.payload
      if (
        state?.filteredComponentValue &&
        state.filteredComponentValue.interfaceId === interfaceId
      ) {
        delete state.filteredComponentValue
      }
      return {
        ...state,
        ...dropCachedInterfaces(state, [interfaceId])
      }
    }
    case InterfaceActionTypes.REMOVE_APPLICATION_COMPONENT: {
      const { componentId, interfaceId } = action.payload
      const appComponents = state.applicationComponents || {}
      const component = appComponents[interfaceId] || {}
      let filterComponentIds = state.filterComponentIds
      let filterComponentState = state.filterComponentState
      if (component[componentId]?.component_type === ComponentTypes.Core_Filter) {
        filterComponentIds = []
        filterComponentState = []
      }
      delete component[componentId]
      const sizes = state.componentSizes?.[interfaceId]
      let componentSizes = state.componentSizes
      if (sizes && componentId in sizes) {
        const nextSizes = { ...sizes }
        delete nextSizes[componentId]
        componentSizes = { ...state.componentSizes, [interfaceId]: nextSizes }
      }
      return {
        ...state,
        applicationComponents: { ...appComponents },
        componentSizes,
        filterComponentIds,
        filterComponentState
      }
    }
    case InterfaceActionTypes.SET_RELOAD_LAYOUT: {
      return {
        ...state,
        reloadLayout: Math.max(1, (state.reloadLayout || 0) + 1)
      }
    }
    case InterfaceActionTypes.SET_FILTER_COMPONENT_STATE: {
      return {
        ...state,
        filterComponentState: action.payload
      }
    }
    case InterfaceActionTypes.ADD_FILTER_COMPONENT_STATE: {
      const filterComponentState = state.filterComponentState || []
      let filterComponentIds = state.filterComponentIds
      const componentId = action.payload.component_id
      let filterComponent = action.payload
      filterComponent['renderKey'] = randomId()
      const index = filterComponentState.findIndex((item) => item.component_id === componentId)
      let newFilterComponentState
      if (index === -1) {
        newFilterComponentState = [...filterComponentState, filterComponent]
        filterComponentIds = filterComponentIds?.concat(componentId)
      } else {
        newFilterComponentState = filterComponentState.map((item, idx) =>
          idx === index ? filterComponent : item
        )
      }
      return {
        ...state,
        filterComponentState: newFilterComponentState,
        filterComponentIds
      }
    }
    case InterfaceActionTypes.REMOVE_FILTER_COMPONENT_STATE: {
      const filterComponentState = state.filterComponentState || []
      let filterComponentIds = state.filterComponentIds
      const componentId = action.payload
      const index = filterComponentState.findIndex((item) => item.component_id === componentId)
      if (index !== -1) {
        filterComponentState.splice(index, 1)
        filterComponentIds = filterComponentIds?.filter((item) => item !== componentId)
      }
      return {
        ...state,
        filterComponentState,
        filterComponentIds
      }
    }
    case InterfaceActionTypes.UPDATE_FILTER_COMPONENT_STATE_RESULT: {
      const filterComponentStateResult: FilteredComponentValue = action.payload
      const filterComponentState = state.filterComponentState || []
      const updatedFilterComponentState = filterComponentState.map((item) =>
        item.component_id === filterComponentStateResult.componentId
          ? {
              ...item,
              result: {
                ...item.result,
                selected_values: filterComponentStateResult.values,
                ...(filterComponentStateResult.indexes
                  ? { selected_indexes: filterComponentStateResult.indexes }
                  : {})
              }
            }
          : item
      )
      return {
        ...state,
        filterComponentState: updatedFilterComponentState
      }
    }
    case InterfaceActionTypes.ORDER_FILTER_COMPONENT_STATE: {
      const order = action.payload
      const filterComponentState = state.filterComponentState || []
      const orderedFilterComponentState = filterComponentState.sort(
        (a, b) => order.indexOf(a.component_id) - order.indexOf(b.component_id)
      )
      return {
        ...state,
        filterComponentState: orderedFilterComponentState
      }
    }
    case InterfaceActionTypes.SET_FILTER_COMPONENT_DIALOG_OPEN: {
      return {
        ...state,
        filterComponentDialogOpen: action.payload
      }
    }
    case InterfaceActionTypes.SET_OPEN_FILTER_COMPONENT_DIALOG: {
      return {
        ...state,
        openFilterComponentDialog: action.payload
      }
    }
    case InterfaceActionTypes.OPEN_FILE_UPLOAD_ITEM_DIALOG: {
      return {
        ...state,
        openFileUploadItemDialog: action.payload.componentId
      }
    }
    case InterfaceActionTypes.DOWNLOAD_ALL_COMPONENT_FILES: {
      return {
        ...state,
        downloadAllComponentFiles: action.payload.componentId
      }
    }
    case InterfaceActionTypes.SET_UPLOAD_MANAGER_SEARCH_TEXT: {
      return {
        ...state,
        uploadManagerSearchText: action.payload
      }
    }
    default: {
      return state
    }
  }
}

export { reducer as interfaceReducer }
